import { get, post, put } from '@/services/http'
import { endpoints } from '@/services/endpoints'

/**
 * Logbook — the operator's written record. LIVE.
 *
 * `POST /v1/logbook`, `GET /v1/logbook`, `GET /v1/logbook/{id}`, `PUT
 * /v1/logbook/{id}` and both SSE streams, behind `logbook.manage` or
 * `logbook.record_entries`. The `DELETE` is served and not wired.
 *
 * `LogBookEntryOut` does not carry `created_by`, so this client cannot tell
 * which entries a `record_entries` holder is allowed to change. Edit is offered
 * to every reader and the server's 403 is said plainly — see `useLogbookAccess`.
 *
 * ── This file is the seam ─────────────────────────────────────────────────
 * The enums below live here and the page imports them from here, never from
 * `mocks/`. They are the server's own `LogBookCategory` and `LogBookSeverity`,
 * not a design's list — a value this app invented would be a 422 on the first
 * real write.
 *
 * ── An entry is editable, and says so ─────────────────────────────────────
 * The model's own comment says append-only; `LogBookEntryUpdate` exists anyway
 * and the schema wins. An edited entry is marked "Edited <when>" on its detail,
 * so a reader citing it can see it was changed after it was written.
 */

/** The two ways the log is read. Routes-in-search-params, not `useState`. */
export const LOGBOOK_VIEWS = ['feed', 'timeline']

/**
 * `LogBookCategory`, all eight of it, in the order a form should offer them.
 *
 * Observation first because it is the ordinary case — the note somebody writes
 * when nothing is wrong yet, which is most of what a logbook holds and the only
 * kind an investigation finds surprising.
 */
export const LOGBOOK_CATEGORIES = [
  'observation',
  'fault',
  'maintenance',
  'changeover',
  'quality',
  'safety',
  'handover_note',
  'process_change',
]

/** `LogBookSeverity`. Severity-first for display, as the work-order board does. */
export const LOGBOOK_SEVERITIES = ['critical', 'high', 'medium', 'low']

/** The default a new entry opens on. Neither end of the scale is a safe guess. */
export const DEFAULT_CATEGORY = 'observation'
export const DEFAULT_SEVERITY = 'low'

/** `Field(min_length=1, max_length=255)` and `(1, 2000)` on the two text columns. */
export const TITLE_MAX_LENGTH = 255
export const OBSERVATION_MAX_LENGTH = 2000

/** The largest page the server will serve — `Query(..., le=200)`. */
export const MAX_PAGE_SIZE = 200

/** What the feed asks for. A logbook is read in runs, not paged through. */
export const DEFAULT_PAGE_SIZE = 50

/*
 * ── The DEMO log's two enums, kept for the reason the recipe says ─────────
 * `LOGBOOK_SHIFTS` was the roster's three shift types, imported by the timeline
 * for its dot colours. The live `shift_id` is a shift TEMPLATE from Shift
 * Master and carries `ShiftType` — six members, not three — so the timeline
 * reads the pattern's own type now and this list would colour four of them
 * wrong. Restoring the demo timeline means uncommenting this and the block in
 * `LogbookTimeline`.
 *
 * export const LOGBOOK_SHIFTS = ['morning', 'afternoon', 'night']
 */

/**
 * Wire → app.
 *
 * The list item and the detail share every field but three: `observation`,
 * `photos` and `sensor_tags` are detail-only, and the list sends `photo_count`
 * and `sensor_tag_count` in their place. Both go through here, so a row from
 * either shape is the same object with the absent halves left null — rather
 * than two mappers free to disagree about what a `hierarchy` is.
 *
 * ── What is NOT here, because `LogBookEntryOut` does not carry it ─────────
 * The AUTHOR. `created_by` is on the row server-side and is not serialised, so
 * the feed cannot print who wrote a note — which is most of why a logbook has
 * a byline. Recorded in BACKEND-GAPS.md. Nothing here invents one.
 */
export const toEntry = (row) => ({
  id: row.id,
  /* The number a person reads and quotes — `LOG-0001`. Distinct from `id`,
     which is the row key, so nothing renders `id`. */
  serialNumber: row.serial_number ?? row.id,

  title: row.title ?? '',
  /* Detail only. Null rather than `''` on a list row: "not fetched" and "the
     author wrote nothing" are different answers, and the column is `min_length=1`
     so the second one cannot happen. */
  observation: row.observation ?? null,

  category: row.category,
  severity: row.severity,

  shiftId: row.shift_id ?? null,
  /* A calendar date, `YYYY-MM-DD`. Not an instant: `shift_date` is a `date`
     column and the day is the whole of the fact, so formatting it against a
     timezone would move a night shift across midnight for half the estate. */
  shiftDate: row.shift_date ?? null,

  plantId: row.plant_id ?? null,
  lineId: row.line_id ?? null,
  machineId: row.machine_id ?? null,
  /* `{plant, line, machine}` — names only. `hierarchy_names` serves no ids, so
     nothing may link off one; the asset's own record is where that lives. */
  hierarchy: row.hierarchy ?? {},

  anomalyId: row.anomaly_id ?? null,
  workOrderId: row.work_order_id ?? null,

  /* Detail only. The `*_detail` briefs are embedded so a reader without the
     other module's grant still sees WHICH item is linked. A brief carries no id
     of its own — the `*_id` beside it is the link target. Null on a list row,
     and null on a detail whose link is gone (deleted, or never set). */
  shift: row.shift_detail
    ? { name: row.shift_detail.name, type: row.shift_detail.type, active: row.shift_detail.is_active }
    : null,
  anomaly: row.anomaly_detail
    ? {
        serialNumber: row.anomaly_detail.serial_number,
        title: row.anomaly_detail.title,
        severity: row.anomaly_detail.severity,
        status: row.anomaly_detail.status,
      }
    : null,
  workOrder: row.work_order_detail
    ? {
        serialNumber: row.work_order_detail.serial_number,
        title: row.work_order_detail.title,
        status: row.work_order_detail.status,
        priority: row.work_order_detail.priority,
      }
    : null,

  /* Two shapes of the same two facts. A list row knows HOW MANY photos and tags
     an entry has; a detail row knows which. Both are carried so a card can show
     a count from either without asking which endpoint it came from. */
  photoCount: row.photo_count ?? (row.photos?.length ?? 0),
  sensorTagCount: row.sensor_tag_count ?? (row.sensor_tags?.length ?? 0),
  photos: (row.photos ?? []).map((photo) => ({
    fileId: photo.logbook_file_id,
    /* A presigned address with a short life. Resolved per request server-side,
       so it must not be cached past the response that carried it. */
    viewUrl: photo.logbook_view_url,
  })),
  sensorTags: (row.sensor_tags ?? []).map((tag) => ({
    id: tag.id,
    sensorTagId: tag.sensor_tag_id,
    name: tag.name ?? '',
    description: tag.description ?? '',
    valueType: tag.value_type,
    /* The tag's sensor CONNECTION's flag: a paused connection keeps its pins
       on the entry, flagged rather than hidden. */
    active: tag.is_active ?? true,
    windowStart: tag.window_start,
    windowEnd: tag.window_end,
  })),

  createdAt: row.created_at ?? null,
  updatedAt: row.updated_at ?? null,
})

/**
 * A `datetime-local` value, as the naive UTC the API demands.
 *
 * ── This conversion is load-bearing and easy to skip ─────────────────────
 * `NaiveUTCDatetime` REJECTS an offset or a trailing `Z` — the column is
 * `DateTime(timezone=False)` and stores UTC, so an aware value would silently
 * mean something else once written. Meanwhile `<input type="datetime-local">`
 * yields `2026-09-29T14:30` in the READER'S OWN ZONE with no marker saying so.
 *
 * Sending that string through unchanged therefore passes validation and stores
 * the wrong instant — by five and a half hours in Asia/Kolkata, which is the
 * only kind of bug a sensor window has: it looks right, and it reads the wrong
 * minutes off the trace.
 *
 * So the local value is parsed as local, shifted to UTC, and serialised without
 * a zone marker. Seconds are included because the column has them.
 */
export function toNaiveUtc(localValue) {
  if (!localValue) return null
  const local = new Date(localValue)
  if (Number.isNaN(local.getTime())) return null
  return new Date(local.getTime() - local.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 19)
}

/**
 * Back the other way — naive UTC → the local string a `datetime-local` wants.
 *
 * The API's value has no zone marker, which `Date` reads as LOCAL rather than
 * UTC. So the `Z` is appended before parsing; without it the round trip drifts
 * by the offset in whichever direction the reader sits.
 */
export function fromNaiveUtc(wireValue) {
  if (!wireValue) return ''
  const utc = new Date(`${wireValue.replace(' ', 'T').replace(/Z$/, '')}Z`)
  if (Number.isNaN(utc.getTime())) return ''
  return new Date(utc.getTime() - utc.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
}

/** Today as `YYYY-MM-DD`, in the reader's own zone — the day a shift date means. */
export function todayISO() {
  const now = new Date()
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10)
}

/**
 * The log, filtered.
 *
 * Params are snake_case because this is a real FastAPI signature and a
 * camelCase key is silently IGNORED — the server would answer the unfiltered
 * page 1 and the screen would show the wrong rows under the right heading.
 *
 * ── The counts are taken before two of the filters ───────────────────────
 * `counts.category` and `counts.severity` are computed off the scoped board
 * BEFORE `category` and `severity` apply, and after the asset and date ones.
 * That is exactly what a chip row needs: the numbers describe what is there to
 * narrow to, so pressing one moves the rows and not the figures above them.
 * It also means a chip may read a count that the visible list does not match,
 * which is how a reader learns a filter is on.
 *
 * @param {{
 *   category?: string, severity?: string, plantId?: string, lineId?: string,
 *   machineId?: string, shiftId?: string,
 *   anomalyId?: string, workOrderId?: string, from?: string, to?: string,
 *   page?: number, pageSize?: number,
 * }} params
 */
export async function fetchLogbook(params = {}, signal) {
  const {
    category,
    severity,
    plantId,
    lineId,
    machineId,
    shiftId,
    anomalyId,
    workOrderId,
    from,
    to,
    page = 1,
    pageSize = DEFAULT_PAGE_SIZE,
  } = params

  const data = await get(endpoints.logbook.list, {
    signal,
    params: {
      // `all` is this screen's sentinel for an unset filter; the wire wants the
      // key absent, not the word.
      ...(category && category !== 'all' ? { category } : {}),
      ...(severity && severity !== 'all' ? { severity } : {}),
      ...(plantId ? { plant_id: plantId } : {}),
      ...(lineId ? { line_id: lineId } : {}),
      ...(machineId && machineId !== 'all' ? { machine_id: machineId } : {}),
      ...(shiftId ? { shift_id: shiftId } : {}),
      ...(anomalyId ? { anomaly_id: anomalyId } : {}),
      ...(workOrderId ? { work_order_id: workOrderId } : {}),
      ...(from ? { shift_date_from: from } : {}),
      ...(to ? { shift_date_to: to } : {}),
      /* Newest first, which is the only order a logbook is read in. `created_at`
         and not `shift_date`: two notes written eight hours apart on the same
         night share a shift date, and sorting on it would leave their order to
         the tiebreaker. */
      sort_by: 'created_at',
      sort_dir: 'desc',
      page,
      page_size: pageSize,
    },
  })

  return {
    items: (data?.logbook_entries ?? []).map(toEntry),
    // Echoed back by the server rather than assumed from what was asked for: a
    // clamped or defaulted page must move the footer, not be contradicted by it.
    page: data?.page ?? page,
    pageSize: data?.page_size ?? pageSize,
    total: data?.total ?? 0,
    totalPages: data?.total_pages ?? 0,
    counts: {
      category: data?.counts?.category ?? {},
      severity: data?.counts?.severity ?? {},
    },
  }
}

/**
 * One entry — `GET /v1/logbook/{id}`, the full `LogBookEntryOut`.
 *
 * Scope-checked server-side: an entry outside the reader's grant is a 404, not
 * a 403, so the screen cannot tell "gone" from "not yours" and does not try.
 */
export async function fetchLogbookEntry(id, signal) {
  return toEntry(await get(endpoints.logbook.detail(id), { signal }))
}

/**
 * Write an entry.
 *
 * ── The author is NOT sent, and now genuinely cannot be ──────────────────
 * `LogBookEntryCreate` has no author field at all: the server stamps
 * `created_by` from the token. A client that could name the author could sign
 * somebody else's observation, which is the same rule that keeps `source` off
 * a work-order create.
 *
 * ── Every key is PRESENT, including the null ones ────────────────────────
 * `anomaly_id` and `work_order_id` are `uuid | None` with NO default, and
 * omitting a nullable key is a 422 in this API rather than a null. It has cost
 * four creates historically. `photo_file_ids` and `sensor_tags` are the same
 * shape with `[]` as the empty answer.
 *
 * ── The parent, the shift and the date are Create-only ───────────────────
 * `_LogBookFields` holds neither the parent trio nor `shift_id`/`shift_date`,
 * and the schema says why: which asset an entry is about and which shift it was
 * logged in are facts, not editable. `ExactlyOneParentMixin` refuses zero or
 * two parents — as a Pydantic `model_validator`, which returns FastAPI's 422
 * shape rather than the field-keyed envelope — so the form guarantees exactly
 * one before sending.
 */
export async function createLogbookEntry({
  plantId,
  lineId,
  machineId,
  shiftId,
  shiftDate,
  category,
  severity,
  title,
  observation,
  anomalyId,
  workOrderId,
  photoFileIds,
  sensorTags,
}) {
  return toEntry(
    await post(endpoints.logbook.list, {
      // Exactly one of the three is set, and all three keys are present.
      plant_id: plantId ?? null,
      line_id: lineId ?? null,
      machine_id: machineId ?? null,

      shift_id: shiftId,
      // A calendar date, `YYYY-MM-DD`, which is what the column is.
      shift_date: shiftDate,

      category,
      severity,
      title: title.trim(),
      observation: observation.trim(),

      anomaly_id: anomalyId || null,
      work_order_id: workOrderId || null,

      photo_file_ids: photoFileIds ?? [],
      /* Each window goes out as naive UTC — see `toNaiveUtc` for why the
         browser's own `datetime-local` string must not be sent through. */
      sensor_tags: (sensorTags ?? []).map((tag) => ({
        sensor_tag_id: tag.sensorTagId,
        window_start: toNaiveUtc(tag.windowStart),
        window_end: toNaiveUtc(tag.windowEnd),
      })),
    }),
  )
}

/**
 * Change an entry — `PUT /v1/logbook/{id}`, `LogBookEntryUpdate`.
 *
 * ── A FULL replace, not a patch ──────────────────────────────────────────
 * The REST route passes every `_LogBookFields` key to the action, so a list
 * sent empty is a list emptied: `photo_file_ids: []` detaches every photo and
 * `sensor_tags: []` unpins every window. The form therefore seeds both from the
 * entry and sends them back whole, including tags a reader without
 * `sensors.manage_sensors` cannot see the picker for.
 *
 * ── What is NOT sent ─────────────────────────────────────────────────────
 * The parent trio, `shift_id` and `shift_date` are Create-only. Sending any of
 * them is a 400 under `extra="forbid"`, not an ignored key.
 *
 * Same as the create, every key is present: `anomaly_id` and `work_order_id`
 * are nullable with no default, and omitting one is a 422.
 */
export async function updateLogbookEntry({
  id,
  category,
  severity,
  title,
  observation,
  anomalyId,
  workOrderId,
  photoFileIds,
  sensorTags,
}) {
  return toEntry(
    await put(endpoints.logbook.detail(id), {
      category,
      severity,
      title: title.trim(),
      observation: observation.trim(),
      anomaly_id: anomalyId || null,
      work_order_id: workOrderId || null,
      photo_file_ids: photoFileIds ?? [],
      sensor_tags: (sensorTags ?? []).map((tag) => ({
        sensor_tag_id: tag.sensorTagId,
        window_start: windowOut(tag.windowStart, tag.seed?.start),
        window_end: windowOut(tag.windowEnd, tag.seed?.end),
      })),
    }),
  )
}

/**
 * A window end, as the wire wants it.
 *
 * A seeded row carries the server's own value beside the minute-precision
 * string the control shows. Untouched, the server's value goes back verbatim:
 * the round trip through `datetime-local` drops seconds, and a window whose
 * seconds moved is a DIFFERENT row to `sync_child_collection` — keyed on
 * `(sensor_tag_id, window_start, window_end)` — so it would be deleted and
 * re-created on a save that changed nothing about it.
 */
function windowOut(localValue, seed) {
  if (seed && seed.local === localValue) return seed.wire
  return toNaiveUtc(localValue)
}
